import type { IPointOfSale } from '../shared/models/core/point-of-sale';
import type { ISalesOffice } from '../shared/models/core/sales-office';
import type { OfflineCorrelative } from './types';
import { mergeCorrelativesForPointOfSale, setOfflineCache } from './db';

type LowPriorityRequestInit = RequestInit & { priority?: 'low' };

export interface OfflineCatalogSnapshot {
  correlativesRaw: OfflineCorrelative[] | { results?: OfflineCorrelative[] };
  identificationTypesRaw: unknown;
  ubigeosRaw: unknown;
  sellersRaw: unknown;
  exchangeRate: number;
  freightsRaw: unknown;
  banksRaw?: unknown;
  receiptTypesRaw?: unknown;
  taxExemptionReasonTypesRaw?: unknown;
  companiesRaw?: unknown;
  marketsRaw?: unknown;
  currenciesRaw?: unknown;
  zonesRaw?: unknown;
  salesOfficesRaw?: ISalesOffice[];
  invoiceTemplateHtml?: string;
  ticketTemplateHtml?: string;
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const lowPriorityInit = (accept: string): LowPriorityRequestInit => ({
  credentials: 'include',
  cache: 'no-store',
  headers: { Accept: accept },
  priority: 'low',
});

const getJson = async <T>(url: string): Promise<T> => {
  const response = await fetch(url, lowPriorityInit('application/json'));
  if (!response.ok) throw new Error(`No se pudo sincronizar ${url} (${response.status}).`);
  return (await response.json()) as T;
};

const getText = async (url: string): Promise<string> => {
  const response = await fetch(url, lowPriorityInit('text/html, text/plain;q=0.9, */*;q=0.8'));
  if (!response.ok) throw new Error(`No se pudo sincronizar ${url} (${response.status}).`);
  return response.text();
};

const getOptionalJson = async <T>(url: string): Promise<T | undefined> => {
  try {
    return await getJson<T>(url);
  } catch (error) {
    console.warn(`No se pudo precargar el catálogo opcional ${url}.`, error);
    return undefined;
  }
};

const getOptionalText = async (url: string): Promise<string | undefined> => {
  try {
    return await getText(url);
  } catch (error) {
    console.warn(`No se pudo precargar la plantilla ${url}.`, error);
    return undefined;
  }
};

const resultsOf = <T>(value: T[] | { results?: T[] }): T[] =>
  Array.isArray(value) ? value : (value.results ?? []);

const asArray = <T>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (
    value &&
    typeof value === 'object' &&
    Array.isArray((value as { results?: unknown[] }).results)
  ) {
    return (value as { results: T[] }).results;
  }
  return [];
};

/**
 * Descarga una sola vez por ciclo los datos pequeños/comunes necesarios para operar sin red.
 * El snapshot luego se materializa de forma independiente para cada PV asociado al usuario.
 * Stock/productos se sincronizan aparte porque son el volumen grande y deben paginarse.
 */
export const loadOfflineCatalogSnapshot = async (): Promise<OfflineCatalogSnapshot> => {
  const correlativesRaw = await getJson<OfflineCorrelative[] | { results?: OfflineCorrelative[] }>(
    '/api/correlatives?size=-1'
  );
  await sleep(150);
  const identificationTypesRaw = await getJson<unknown>(
    '/api/identificationdocumenttypes?enabled=true&size=-1'
  );
  await sleep(150);
  const exchangeRate = await getJson<number>('/api/exchangerates');
  await sleep(200);
  const ubigeosRaw = await getJson<unknown>('/api/ubigeos?Size=10000');
  await sleep(250);
  const sellersRaw = await getJson<unknown>('/api/users/sellers?size=-1');
  await sleep(250);
  const freightsRaw = await getJson<unknown>('/api/fletes?size=-1');

  // El resto son catálogos de soporte. Se consultan secuencialmente y con prioridad baja para
  // que la preparación de otros PV no compita con las operaciones normales del usuario.
  await sleep(150);
  const banksRaw = await getOptionalJson<unknown>('/api/banks?size=-1');
  await sleep(150);
  const receiptTypesRaw = await getOptionalJson<unknown>('/api/receiptTypes?size=-1');
  await sleep(150);
  const taxExemptionReasonTypesRaw = await getOptionalJson<unknown>(
    '/api/taxExemptionReasonTypes?size=-1'
  );
  await sleep(150);
  const companiesRaw = await getOptionalJson<unknown>('/api/companies?size=-1');
  await sleep(150);
  const marketsRaw = await getOptionalJson<unknown>('/api/markets?size=-1');
  await sleep(150);
  const currenciesRaw = await getOptionalJson<unknown>('/api/currencyTypes?size=-1');
  await sleep(150);
  const zonesRaw = await getOptionalJson<unknown>('/api/zones?size=-1');
  await sleep(150);
  const salesOfficesRaw = await getOptionalJson<ISalesOffice[]>('/api/salesoffices');

  // Son las mismas plantillas oficiales utilizadas por la impresión actual. Se conservan en
  // IndexedDB para que el renderer offline no tenga que mantener un formato alternativo.
  await sleep(150);
  const invoiceTemplateHtml = await getOptionalText('/api/receipts/templates/invoice2');
  await sleep(150);
  const ticketTemplateHtml = await getOptionalText('/api/receipts/templates/ticket2');

  return {
    correlativesRaw,
    identificationTypesRaw,
    ubigeosRaw,
    sellersRaw,
    exchangeRate,
    freightsRaw,
    banksRaw,
    receiptTypesRaw,
    taxExemptionReasonTypesRaw,
    companiesRaw,
    marketsRaw,
    currenciesRaw,
    zonesRaw,
    salesOfficesRaw,
    invoiceTemplateHtml,
    ticketTemplateHtml,
  };
};

/**
 * Materializa el snapshot para un PV. Cada PV conserva su copia lógica de configuración y
 * catálogos, aunque los datos compartidos se hayan descargado una sola vez en el ciclo.
 */
export const syncOfflineCatalogs = async (
  pointOfSale: IPointOfSale,
  snapshot?: OfflineCatalogSnapshot
): Promise<void> => {
  if (!pointOfSale.isOffline || pointOfSale.isDealer) return;

  const data = snapshot ?? (await loadOfflineCatalogSnapshot());

  await mergeCorrelativesForPointOfSale(resultsOf(data.correlativesRaw), pointOfSale);

  const completedAt = new Date().toISOString();
  const salesOfficesForCompany = (data.salesOfficesRaw ?? []).filter(
    (office) =>
      !!pointOfSale.companyCode &&
      office.companyCode?.trim().toLowerCase() === pointOfSale.companyCode.trim().toLowerCase()
  );

  const writes: Promise<void>[] = [
    setOfflineCache(`identificationTypes:${pointOfSale.id}`, data.identificationTypesRaw),
    setOfflineCache(`ubigeos:${pointOfSale.id}`, data.ubigeosRaw),
    setOfflineCache(`sellers:${pointOfSale.id}`, data.sellersRaw),
    setOfflineCache(`exchangeRate:${pointOfSale.id}`, pointOfSale.isDealer ? 1 : data.exchangeRate),
    setOfflineCache(`freights:${pointOfSale.id}`, data.freightsRaw),
    setOfflineCache(`pointOfSale:${pointOfSale.id}`, pointOfSale),
    setOfflineCache(`salesOffices:${pointOfSale.id}`, salesOfficesForCompany),
    setOfflineCache(`catalogSync:${pointOfSale.id}`, {
      lastSyncAt: completedAt,
      companyTaxId: pointOfSale.companyTaxId,
      marketId: pointOfSale.marketId,
      marketCode: pointOfSale.marketCode,
    }),
  ];

  if (data.banksRaw !== undefined) {
    writes.push(setOfflineCache(`banks:${pointOfSale.id}`, data.banksRaw));
  }
  if (data.receiptTypesRaw !== undefined) {
    writes.push(setOfflineCache(`receiptTypes:${pointOfSale.id}`, data.receiptTypesRaw));
  }
  if (data.taxExemptionReasonTypesRaw !== undefined) {
    writes.push(
      setOfflineCache(`taxExemptionReasonTypes:${pointOfSale.id}`, data.taxExemptionReasonTypesRaw)
    );
  }
  if (data.companiesRaw !== undefined) {
    const companies = asArray<Record<string, unknown>>(data.companiesRaw);
    const company = companies.find(
      (item) => String(item.taxId ?? item.companyTaxId ?? '') === pointOfSale.companyTaxId
    );
    writes.push(setOfflineCache(`companies:${pointOfSale.id}`, data.companiesRaw));
    if (company) writes.push(setOfflineCache(`company:${pointOfSale.id}`, company));
  }
  if (data.marketsRaw !== undefined) {
    const markets = asArray<Record<string, unknown>>(data.marketsRaw);
    const market = markets.find(
      (item) =>
        String(item.id ?? '') === String(pointOfSale.marketId ?? '') ||
        String(item.code ?? item.marketCode ?? '') === pointOfSale.marketCode
    );
    writes.push(setOfflineCache(`markets:${pointOfSale.id}`, data.marketsRaw));
    if (market) writes.push(setOfflineCache(`market:${pointOfSale.id}`, market));
  }
  if (data.currenciesRaw !== undefined) {
    writes.push(setOfflineCache(`currencies:${pointOfSale.id}`, data.currenciesRaw));
  }
  if (data.zonesRaw !== undefined) {
    writes.push(setOfflineCache(`zones:${pointOfSale.id}`, data.zonesRaw));
  }
  if (data.invoiceTemplateHtml) {
    writes.push(
      setOfflineCache(`receiptTemplate:invoice2:${pointOfSale.id}`, data.invoiceTemplateHtml)
    );
  }
  if (data.ticketTemplateHtml) {
    writes.push(
      setOfflineCache(`receiptTemplate:ticket2:${pointOfSale.id}`, data.ticketTemplateHtml)
    );
  }

  await Promise.all(writes);
};
